import { forwardRef, ReactNode } from 'react';
import {
  Box,
  Group,
  Stack,
  Text,
  Avatar,
  Badge,
  ActionIcon,
  Menu,
  Tooltip,
  Paper,
} from '@mantine/core';
import {
  IconMail,
  IconPhone,
  IconMapPin,
  IconBriefcase,
  IconBuilding,
  IconDots,
  IconEdit,
  IconTrash,
  IconMessage,
  IconCalendar,
  IconUserPlus,
} from '@tabler/icons-react';
import { Card, Button } from '../../ui';
import { FadeIn, HoverLift } from '../../animations';
import { usePreferencesStore } from '../../../stores';
import { LINE_HEIGHT_MAP, LETTER_SPACING_MAP } from '../../../hooks/useAccessibilityStyles';

export interface PersonData {
  id: string;
  firstName: string;
  lastName: string;
  email?: string;
  phone?: string;
  avatar?: string;
  role?: string;
  department?: string;
  organization?: string;
  location?: string;
  status?: 'active' | 'inactive' | 'away' | 'busy';
  tags?: string[];
  metadata?: Record<string, unknown>;
}

export interface PersonCardProps {
  person: PersonData;
  variant?: 'default' | 'compact' | 'horizontal' | 'detailed';
  showContact?: boolean;
  showStatus?: boolean;
  showActions?: boolean;
  onView?: () => void;
  onEdit?: () => void;
  onDelete?: () => void;
  onMessage?: () => void;
  onSchedule?: () => void;
  onClick?: () => void;
  customActions?: {
    key: string;
    label: string;
    icon?: ReactNode;
    onClick: () => void;
    color?: string;
  }[];
  className?: string;
}

export const PersonCard = forwardRef<HTMLDivElement, PersonCardProps>(
  (
    {
      person,
      variant = 'default',
      showContact = true,
      showStatus = true,
      showActions = true,
      onView,
      onEdit,
      onDelete,
      onMessage,
      onSchedule,
      onClick,
      customActions = [],
      className,
    },
    ref
  ) => {
    const fullName = `${person.firstName} ${person.lastName}`;

    const lineSpacing = usePreferencesStore((state) => state.lineSpacing);
    const letterSpacingPref = usePreferencesStore((state) => state.letterSpacing);

    const accessibilityStyles = {
      lineHeight: LINE_HEIGHT_MAP[lineSpacing],
      letterSpacing: LETTER_SPACING_MAP[letterSpacingPref],
    };

    const getStatusColor = (status?: string) => {
      switch (status) {
        case 'active':
          return 'green';
        case 'inactive':
          return 'gray';
        case 'away':
          return 'yellow';
        case 'busy':
          return 'red';
        default:
          return 'gray';
      }
    };

    const renderActionsMenu = () => (
      <Menu position="bottom-end" withinPortal>
        <Menu.Target>
          <ActionIcon variant="subtle" onClick={(e) => e.stopPropagation()}>
            <IconDots size={16} />
          </ActionIcon>
        </Menu.Target>
        <Menu.Dropdown>
          {onMessage && (
            <Menu.Item leftSection={<IconMessage size={14} />} onClick={onMessage}>
              Send Message
            </Menu.Item>
          )}
          {onSchedule && (
            <Menu.Item leftSection={<IconCalendar size={14} />} onClick={onSchedule}>
              Schedule Meeting
            </Menu.Item>
          )}
          {customActions.map((action) => (
            <Menu.Item
              key={action.key}
              leftSection={action.icon}
              color={action.color}
              onClick={action.onClick}
            >
              {action.label}
            </Menu.Item>
          ))}
          {(onEdit || onDelete) && <Menu.Divider />}
          {onEdit && (
            <Menu.Item leftSection={<IconEdit size={14} />} onClick={onEdit}>
              Edit
            </Menu.Item>
          )}
          {onDelete && (
            <Menu.Item leftSection={<IconTrash size={14} />} color="red" onClick={onDelete}>
              Delete
            </Menu.Item>
          )}
        </Menu.Dropdown>
      </Menu>
    );

    if (variant === 'compact') {
      return (
        <FadeIn>
          <HoverLift lift={2}>
            <Paper
              ref={ref}
              className={className}
              p="sm"
              withBorder
              style={{ cursor: onClick ? 'pointer' : 'default' }}
              onClick={onClick}
            >
              <Group justify="space-between">
                <Group gap="sm">
                  <Box style={{ position: 'relative' }}>
                    <Avatar src={person.avatar} size="sm" radius="xl">
                      {fullName.charAt(0)}
                    </Avatar>
                    {showStatus && person.status && (
                      <Box
                        style={{
                          position: 'absolute',
                          bottom: 0,
                          right: 0,
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          backgroundColor: `var(--mantine-color-${getStatusColor(person.status)}-5)`,
                          border: '2px solid white',
                        }}
                      />
                    )}
                  </Box>
                  <Box>
                    <Text size="sm" fw={500} style={accessibilityStyles}>{fullName}</Text>
                    {person.role && <Text size="xs" c="dimmed" style={accessibilityStyles}>{person.role}</Text>}
                  </Box>
                </Group>
                {showActions && renderActionsMenu()}
              </Group>
            </Paper>
          </HoverLift>
        </FadeIn>
      );
    }

    if (variant === 'horizontal') {
      return (
        <FadeIn>
          <HoverLift lift={4}>
            <Card
              ref={ref}
              className={className}
              style={{ cursor: onClick ? 'pointer' : 'default' }}
              onClick={onClick}
            >
              <Group justify="space-between">
                <Group gap="md">
                  <Box style={{ position: 'relative' }}>
                    <Avatar src={person.avatar} size="lg" radius="xl">
                      {fullName.charAt(0)}
                    </Avatar>
                    {showStatus && person.status && (
                      <Box
                        style={{
                          position: 'absolute',
                          bottom: 2,
                          right: 2,
                          width: 12,
                          height: 12,
                          borderRadius: '50%',
                          backgroundColor: `var(--mantine-color-${getStatusColor(person.status)}-5)`,
                          border: '2px solid white',
                        }}
                      />
                    )}
                  </Box>
                  <Box>
                    <Group gap="xs">
                      <Text fw={600} style={accessibilityStyles}>{fullName}</Text>
                      {person.tags?.slice(0, 2).map((tag) => (
                        <Badge key={tag} size="xs" variant="light">{tag}</Badge>
                      ))}
                    </Group>
                    {person.role && (
                      <Text size="sm" c="dimmed" style={accessibilityStyles}>
                        {person.role}
                        {person.department && ` • ${person.department}`}
                      </Text>
                    )}
                  </Box>
                </Group>
                <Group gap="lg">
                  {showContact && (
                    <>
                      {person.email && (
                        <Tooltip label={person.email}>
                          <ActionIcon variant="subtle" component="a" href={`mailto:${person.email}`}>
                            <IconMail size={16} />
                          </ActionIcon>
                        </Tooltip>
                      )}
                      {person.phone && (
                        <Tooltip label={person.phone}>
                          <ActionIcon variant="subtle" component="a" href={`tel:${person.phone}`}>
                            <IconPhone size={16} />
                          </ActionIcon>
                        </Tooltip>
                      )}
                    </>
                  )}
                  {showActions && renderActionsMenu()}
                </Group>
              </Group>
            </Card>
          </HoverLift>
        </FadeIn>
      );
    }

    if (variant === 'detailed') {
      return (
        <FadeIn>
          <Card ref={ref} className={className}>
            <Stack gap="md">
              {/* Header */}
              <Group justify="space-between">
                <Group gap="md">
                  <Box style={{ position: 'relative' }}>
                    <Avatar src={person.avatar} size={80} radius="md">
                      {fullName.charAt(0)}
                    </Avatar>
                    {showStatus && person.status && (
                      <Badge
                        color={getStatusColor(person.status)}
                        size="xs"
                        style={{ position: 'absolute', bottom: -4, right: -4 }}
                      >
                        {person.status}
                      </Badge>
                    )}
                  </Box>
                  <Box>
                    <Text size="xl" fw={600} style={accessibilityStyles}>{fullName}</Text>
                    {person.role && (
                      <Group gap="xs">
                        <IconBriefcase size={14} color="gray" />
                        <Text size="sm" c="dimmed" style={accessibilityStyles}>{person.role}</Text>
                      </Group>
                    )}
                    {person.department && (
                      <Group gap="xs">
                        <IconBuilding size={14} color="gray" />
                        <Text size="sm" c="dimmed" style={accessibilityStyles}>{person.department}</Text>
                      </Group>
                    )}
                  </Box>
                </Group>
                {showActions && (
                  <Group gap="xs">
                    {onMessage && (
                      <Button variant="subtle" leftSection={<IconMessage size={16} />} onClick={onMessage}>
                        Message
                      </Button>
                    )}
                    {renderActionsMenu()}
                  </Group>
                )}
              </Group>

              {/* Contact info */}
              {showContact && (person.email || person.phone || person.location) && (
                <Stack gap="xs">
                  {person.email && (
                    <Group gap="xs">
                      <IconMail size={16} color="gray" />
                      <Text size="sm" component="a" href={`mailto:${person.email}`} style={accessibilityStyles}>
                        {person.email}
                      </Text>
                    </Group>
                  )}
                  {person.phone && (
                    <Group gap="xs">
                      <IconPhone size={16} color="gray" />
                      <Text size="sm" component="a" href={`tel:${person.phone}`} style={accessibilityStyles}>
                        {person.phone}
                      </Text>
                    </Group>
                  )}
                  {person.location && (
                    <Group gap="xs">
                      <IconMapPin size={16} color="gray" />
                      <Text size="sm" style={accessibilityStyles}>{person.location}</Text>
                    </Group>
                  )}
                </Stack>
              )}

              {/* Tags */}
              {person.tags && person.tags.length > 0 && (
                <Group gap="xs">
                  {person.tags.map((tag) => (
                    <Badge key={tag} variant="light">{tag}</Badge>
                  ))}
                </Group>
              )}
            </Stack>
          </Card>
        </FadeIn>
      );
    }

    // Default variant
    return (
      <FadeIn>
        <HoverLift lift={4}>
          <Card
            ref={ref}
            className={className}
            style={{ cursor: onClick ? 'pointer' : 'default' }}
            onClick={onClick}
          >
            <Stack gap="md" align="center">
              <Box style={{ position: 'relative' }}>
                <Avatar src={person.avatar} size={64} radius="xl">
                  {fullName.charAt(0)}
                </Avatar>
                {showStatus && person.status && (
                  <Box
                    style={{
                      position: 'absolute',
                      bottom: 2,
                      right: 2,
                      width: 14,
                      height: 14,
                      borderRadius: '50%',
                      backgroundColor: `var(--mantine-color-${getStatusColor(person.status)}-5)`,
                      border: '2px solid white',
                    }}
                  />
                )}
              </Box>
              <Box ta="center">
                <Text fw={600} style={accessibilityStyles}>{fullName}</Text>
                {person.role && <Text size="sm" c="dimmed" style={accessibilityStyles}>{person.role}</Text>}
                {person.organization && (
                  <Text size="xs" c="dimmed" style={accessibilityStyles}>{person.organization}</Text>
                )}
              </Box>
              {showContact && (
                <Group gap="xs">
                  {person.email && (
                    <Tooltip label="Email">
                      <ActionIcon
                        variant="subtle"
                        component="a"
                        href={`mailto:${person.email}`}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <IconMail size={16} />
                      </ActionIcon>
                    </Tooltip>
                  )}
                  {person.phone && (
                    <Tooltip label="Call">
                      <ActionIcon
                        variant="subtle"
                        component="a"
                        href={`tel:${person.phone}`}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <IconPhone size={16} />
                      </ActionIcon>
                    </Tooltip>
                  )}
                  {onMessage && (
                    <Tooltip label="Message">
                      <ActionIcon
                        variant="subtle"
                        onClick={(e) => {
                          e.stopPropagation();
                          onMessage();
                        }}
                      >
                        <IconMessage size={16} />
                      </ActionIcon>
                    </Tooltip>
                  )}
                </Group>
              )}
            </Stack>
          </Card>
        </HoverLift>
      </FadeIn>
    );
  }
);

PersonCard.displayName = 'PersonCard';

export default PersonCard;
